import { useEffect, useMemo, useRef } from "react";

// This exists to remove the issues encountered with non-memorised functions
// Previously, using setTimeout on a callback function would remake the function and lose its ref in the component
// Therefore it would run everytime, as the timeout would be cleared
// This saves the function in a callback
// And the timeout is never lost, so the throttling happens correctly

type UseThrottleDeps = unknown[];
type UseThrottleOptions = { useLeadingCall?: boolean };

type ThrottleCallback<T extends (...args: any[]) => unknown> = T & {
  queryName?: string;
  queryRoute?: string;
};

export const useThrottle = <
  T extends (...args: any[]) => unknown,
  Params extends Parameters<T> = Parameters<T>,
>(
  cb: ThrottleCallback<T>,
  delay: number = 400,
  deps: UseThrottleDeps = [],
  options: UseThrottleOptions = { useLeadingCall: false },
) => {
  const { useLeadingCall } = options;

  const cbRef = useRef(cb);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leadingCallRef = useRef(false);

  // use mutable ref to make useCallback/throttle not depend on `cb` dep
  useEffect(() => {
    cbRef.current = cb;
  }, [cb]);

  const callback = useMemo(() => {
    void deps;

    const throttled = (...args: Params) => {
      if (useLeadingCall && !leadingCallRef.current) {
        leadingCallRef.current = true;
        cbRef.current(...args);
        return;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      timeoutRef.current = setTimeout(() => {
        cbRef.current(...args);
        timeoutRef.current = null;
      }, delay);
    };

    if (cb.queryName) {
      throttled.queryName = cb.queryName;
    }
    if (cb.queryRoute) {
      throttled.queryRoute = cb.queryRoute;
    }

    return throttled as ThrottleCallback<(...args: Params) => ReturnType<T>>;
    // oxlint-disable react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, useLeadingCall, ...deps, cb.queryName, cb.queryRoute]);

  return callback;
};
